import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Pagination from "@/components/Pagination";
import { Link } from "react-router-dom";
import {
  Receipt, MagnifyingGlass, Warning, CheckCircle, ClockCounterClockwise, ArrowSquareOut,
  FilePdf, Image as ImageIcon, XCircle, CurrencyInr, ShieldWarning, ArrowSquareRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmtQty = (n) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n || 0);

const STATUS_META = {
  pending_approval: { label: "Awaiting Approval", cls: "bg-[#FEF7E0] text-[#B06000] border-[#FCE39E]" },
  pending_admin_review: { label: "Awaiting Admin", cls: "bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]" },
  approved: { label: "Approved", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  admin_approved: { label: "Admin Approved", cls: "bg-[#E6F4EA] text-[#137333] border-[#A4D0B3]" },
  rejected: { label: "Rejected", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};

export default function InvoiceManagement() {
  const { user } = useAuth();
  const { activeProjectId, activeProject } = useProject();
  const isAdmin = user?.role === "admin";

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending_count: 0, approved_month_value: 0, high_variance_count: 0 });
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(24);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [highVarOnly, setHighVarOnly] = useState(false);
  const [drawer, setDrawer] = useState(null); // {invoice, po, poInvoices}
  const [drawerLoading, setDrawerLoading] = useState(false);

  const load = async (targetPage = page) => {
    setLoading(true);
    try {
      const params = { page: targetPage, page_size: pageSize };
      if (activeProjectId) params.project_id = activeProjectId;
      if (search) params.search = search;
      if (status !== "all") params.status = status;
      if (highVarOnly) params.high_variance_only = true;
      const { data } = await api.get("/invoices/summary", { params });
      setItems(data.items || []);
      setStats(data.stats || { pending_count: 0, approved_month_value: 0, high_variance_count: 0 });
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
      setPage(data.page || 1);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, highVarOnly, activeProjectId]);

  useEffect(() => {
    const t = setTimeout(() => load(1), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openDrawer = async (row) => {
    setDrawer({ invoice: row, po: null, poInvoices: [] });
    setDrawerLoading(true);
    try {
      const [poRes, invRes] = await Promise.all([
        api.get(`/pos/${row.po_id}`),
        api.get(`/pos/${row.po_id}/invoices`).catch(() => ({ data: [] })),
      ]);
      const poInvoices = Array.isArray(invRes.data) ? invRes.data : invRes.data?.items || [];
      const fromPO = poInvoices.find((i) => i.id === row.id);
      // Merge — start with the /pos/{id}/invoices doc (which has full validation), then overlay
      // the summary row so variance/variance_pct/invoice_number/etc are always present.
      const merged = fromPO ? { ...fromPO, ...row, validation: fromPO.validation } : row;
      setDrawer({ invoice: merged, po: poRes.data, poInvoices });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setDrawer(null);
    } finally {
      setDrawerLoading(false);
    }
  };

  return (
    <div className="p-8 sm:p-10 max-w-[1600px] mx-auto" data-testid="invoice-management-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2 flex items-center gap-2">
            {activeProject && (
              <>
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: activeProject.color || "#0047FF" }} />
                <span>{activeProject.name}</span>
                <span className="text-neutral-300">·</span>
              </>
            )}
            <span>Reconciliation</span>
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight flex items-center gap-3">
            <Receipt size={32} className="text-primary" weight="fill" /> Invoices
          </h1>
          <p className="text-sm text-neutral-500 mt-2">
            {total} invoice{total === 1 ? "" : "s"} — reconcile against POs, spot variances, and approve or reject.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <SummaryCard
          testId="stat-pending"
          icon={<ClockCounterClockwise size={20} weight="duotone" />}
          label="Pending Approval"
          value={stats.pending_count}
          sub="Awaiting reviewer / admin decision"
          accent="text-[#B06000]"
          onClick={() => { setStatus("pending_admin_review"); setPage(1); }}
        />
        <SummaryCard
          testId="stat-approved-month"
          icon={<CurrencyInr size={20} weight="duotone" />}
          label="Approved This Month"
          value={fmtINR(stats.approved_month_value)}
          sub="Rupee value of approvals since the 1st"
          accent="text-[#137333]"
        />
        <SummaryCard
          testId="stat-high-variance"
          icon={<Warning size={20} weight="duotone" />}
          label="High Variance"
          value={stats.high_variance_count}
          sub="Invoice deviates >5% from PO"
          accent="text-[#C5221F]"
          onClick={() => { setHighVarOnly(true); setPage(1); }}
        />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-neutral-200 rounded-sm p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[280px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Search</label>
          <div className="relative">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Invoice #, PO #, vendor, uploader, filename…"
              className="rounded-sm pl-9 h-10"
              data-testid="invoice-mgmt-search"
            />
          </div>
        </div>
        <div className="w-[200px]">
          <label className="text-[11px] uppercase tracking-wider text-neutral-500 mb-1 block">Status</label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-10" data-testid="invoice-mgmt-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending_admin_review">Awaiting Admin</SelectItem>
              <SelectItem value="pending_approval">Awaiting Approval</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="admin_approved">Admin Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 h-10">
          <input
            type="checkbox"
            checked={highVarOnly}
            onChange={(e) => setHighVarOnly(e.target.checked)}
            data-testid="invoice-mgmt-highvar"
          />
          High variance only
        </label>
        {(search || status !== "all" || highVarOnly) && (
          <Button
            variant="ghost"
            className="rounded-sm h-10 text-neutral-500 hover:text-neutral-900"
            onClick={() => { setSearch(""); setStatus("all"); setHighVarOnly(false); }}
            data-testid="invoice-mgmt-clear"
          >
            Clear
          </Button>
        )}
      </div>

      {/* Tile grid */}
      {loading ? (
        <div className="text-center py-16 text-neutral-400">Loading invoices…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-sm p-16 text-center">
          <Receipt size={48} className="mx-auto text-neutral-300 mb-3" />
          <div className="text-sm text-neutral-500">No invoices match the current filters</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((inv) => (
            <InvoiceTile key={inv.id} inv={inv} onOpen={() => openDrawer(inv)} />
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="mt-6 bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          onPageChange={(n) => load(n)}
          testIdPrefix="invoice-mgmt"
        />
      </div>

      {/* Detail modal */}
      <InvoiceDetailModal
        open={!!drawer}
        onOpenChange={(o) => !o && setDrawer(null)}
        drawer={drawer}
        loading={drawerLoading}
        isAdmin={isAdmin}
        onChanged={async () => { await load(page); setDrawer(null); }}
      />
    </div>
  );
}

/* ---------- Summary card ---------- */
function SummaryCard({ icon, label, value, sub, accent, onClick, testId }) {
  return (
    <div
      className={`bg-white border border-neutral-200 rounded-sm p-5 ${onClick ? "cursor-pointer hover:shadow-sm hover:-translate-y-[1px] transition-all" : ""}`}
      onClick={onClick}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-neutral-500 mb-3">
        <span className={accent}>{icon}</span>
        <span>{label}</span>
      </div>
      <div className={`font-display text-3xl font-bold tabular-nums ${accent}`}>{value}</div>
      <div className="text-xs text-neutral-400 mt-1">{sub}</div>
    </div>
  );
}

/* ---------- Invoice tile ---------- */
function InvoiceTile({ inv, onOpen }) {
  const meta = STATUS_META[inv.approval_status] || STATUS_META.pending_approval;
  const isImage = (inv.content_type || "").startsWith("image/");
  const isPdf = (inv.content_type || "").includes("pdf");
  const varianceStyle =
    inv.high_variance ? "text-[#C5221F]" : Math.abs(inv.variance_pct) > 0 ? "text-[#B06000]" : "text-[#137333]";
  return (
    <div
      className="group bg-white border border-neutral-200 rounded-sm overflow-hidden hover:shadow-md hover:-translate-y-[2px] transition-all cursor-pointer"
      onClick={onOpen}
      data-testid={`invoice-tile-${inv.id}`}
    >
      {/* Thumbnail */}
      <div className="relative h-40 bg-neutral-50 border-b border-neutral-100 flex items-center justify-center overflow-hidden">
        {isImage && inv.file_url ? (
          <img src={inv.file_url} alt={inv.invoice_number} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform" />
        ) : isPdf ? (
          <div className="flex flex-col items-center gap-2 text-neutral-400">
            <FilePdf size={36} weight="duotone" />
            <span className="text-[11px] uppercase tracking-wider">PDF</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-neutral-300">
            <ImageIcon size={36} />
            <span className="text-[11px] uppercase tracking-wider">No preview</span>
          </div>
        )}
        <span className={`absolute top-2 left-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border ${meta.cls}`}>
          {meta.label}
        </span>
        {inv.high_variance && (
          <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 border bg-[#FCE8E6] text-[#C5221F] border-[#F3B2AC]">
            <Warning size={10} weight="fill" /> {Math.abs(inv.variance_pct).toFixed(1)}%
          </span>
        )}
      </div>
      {/* Meta */}
      <div className="p-4 space-y-3">
        <div>
          <div className="font-mono-num text-sm text-neutral-900 truncate">{inv.invoice_number || "—"}</div>
          <div className="text-[11px] text-neutral-500 truncate">{inv.vendor_name}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400">Invoice</div>
            <div className="tabular-nums text-neutral-900">{fmtINR(inv.invoice_total)}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-neutral-400">PO</div>
            <div className="tabular-nums text-neutral-600">{fmtINR(inv.po_total)}</div>
          </div>
          <div className="col-span-2 border-t border-neutral-100 pt-2 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wider text-neutral-400">Variance</span>
            <span className={`text-xs font-medium tabular-nums ${varianceStyle}`}>
              {inv.variance >= 0 ? "+" : ""}{fmtINR(inv.variance)} · {inv.variance_pct >= 0 ? "+" : ""}{inv.variance_pct}%
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between text-[11px] text-neutral-400 pt-1">
          <span>{inv.uploaded_at ? new Date(inv.uploaded_at).toLocaleDateString() : "—"}</span>
          <span className="inline-flex items-center gap-1 text-primary font-medium">
            View Details <ArrowSquareRight size={11} />
          </span>
        </div>
      </div>
    </div>
  );
}

/* ---------- Detail modal ---------- */
function InvoiceDetailModal({ open, onOpenChange, drawer, loading, isAdmin, onChanged }) {
  const inv = drawer?.invoice;
  const po = drawer?.po;
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [action, setAction] = useState(null); // 'approve' | 'reject'

  useEffect(() => { setAction(null); setNotes(""); }, [inv?.id]);

  const doAction = async (which) => {
    if (!inv?.id) return;
    if (which === "reject" && !notes.trim()) {
      toast.error("Notes are required to reject an invoice.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/invoices/${inv.id}/${which}`, { notes });
      toast.success(which === "approve" ? "Invoice approved" : "Invoice rejected");
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-6xl p-0 overflow-hidden" data-testid="invoice-detail-modal">
        <DialogHeader className="px-8 pt-8 pb-4 border-b border-neutral-100">
          <DialogTitle className="font-display text-2xl flex items-center gap-3 pr-8">
            <Receipt size={22} className="text-primary" weight="fill" />
            Invoice {inv?.invoice_number || ""}
            {inv?.po_number && (
              <Link
                to={`/pos/${inv.po_id}`}
                className="ml-auto text-primary text-sm font-normal hover:underline inline-flex items-center gap-1"
                data-testid="detail-open-po-link"
              >
                Open PO {inv.po_number} <ArrowSquareOut size={12} />
              </Link>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading || !inv ? (
          <div className="p-16 text-center text-neutral-400">Loading details…</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 max-h-[80vh] overflow-y-auto">
            {/* Left: preview + meta */}
            <div className="border-r border-neutral-100 bg-neutral-50/40 p-6">
              <div className="bg-white border border-neutral-200 rounded-sm h-64 flex items-center justify-center overflow-hidden mb-4">
                {inv.content_type?.startsWith("image/") && inv.file_url ? (
                  <img src={inv.file_url} alt="preview" className="w-full h-full object-contain" />
                ) : inv.content_type?.includes("pdf") ? (
                  <div className="flex flex-col items-center gap-2 text-neutral-500">
                    <FilePdf size={48} weight="duotone" />
                    <a href={inv.file_url} target="_blank" rel="noreferrer" className="text-xs uppercase tracking-wider text-primary underline">
                      Open PDF
                    </a>
                  </div>
                ) : (
                  <div className="text-xs text-neutral-400 uppercase tracking-wider">No preview</div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Vendor" value={inv.vendor_name} />
                <Field label="Uploaded" value={inv.uploaded_at ? new Date(inv.uploaded_at).toLocaleString() : "—"} />
                <Field label="Uploader" value={inv.uploaded_by_name || "—"} />
                <Field label="Status" value={STATUS_META[inv.approval_status]?.label || inv.approval_status} />
                <Field label="Invoice Total" value={fmtINR(inv.invoice_total)} accent="text-neutral-900" />
                <Field label="PO Total" value={fmtINR(inv.po_total)} accent="text-neutral-900" />
                <Field
                  label="Variance"
                  value={`${inv.variance >= 0 ? "+" : ""}${fmtINR(inv.variance)} · ${inv.variance_pct >= 0 ? "+" : ""}${inv.variance_pct}%`}
                  accent={inv.high_variance ? "text-[#C5221F]" : "text-neutral-900"}
                />
                <Field label="AI Verdict" value={inv.ai_verdict || "—"} />
              </div>
            </div>

            {/* Right: line-item mapping */}
            <div className="p-6 space-y-4">
              <div className="text-[11px] uppercase tracking-[0.16em] text-neutral-500 flex items-center gap-2">
                <ShieldWarning size={12} /> Line-item Mapping
              </div>
              <LineItemMapping po={po} validation={inv.validation} />

              {/* Approve / Reject */}
              {inv.approval_status !== "approved" && inv.approval_status !== "admin_approved" && inv.approval_status !== "rejected" && (
                <div className="pt-3 border-t border-neutral-100 space-y-3">
                  {(action === "reject" ||
                    (inv.approval_status === "pending_admin_review" && !isAdmin && action !== "approve")) && (
                    <Textarea
                      placeholder="Notes (required for rejection)…"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="rounded-sm text-sm"
                      rows={2}
                      data-testid="detail-notes"
                    />
                  )}
                  {inv.approval_status === "pending_admin_review" && !isAdmin ? (
                    <div className="text-xs text-[#B06000] flex items-center gap-1">
                      <ShieldWarning size={12} /> Awaiting admin — you cannot act on this invoice.
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        className="rounded-sm h-9 border-[#C5221F] text-[#C5221F] hover:bg-[#FCE8E6] gap-1.5"
                        disabled={submitting}
                        onClick={() => {
                          if (action !== "reject") { setAction("reject"); return; }
                          doAction("reject");
                        }}
                        data-testid="detail-reject-btn"
                      >
                        <XCircle size={14} weight="bold" />
                        {action === "reject" ? "Confirm Reject" : "Reject"}
                      </Button>
                      <Button
                        className="rounded-sm h-9 bg-[#137333] hover:bg-[#0E5C29] text-white gap-1.5"
                        disabled={submitting}
                        onClick={() => doAction("approve")}
                        data-testid="detail-approve-btn"
                      >
                        <CheckCircle size={14} weight="bold" />
                        Approve
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-0.5">{label}</div>
      <div className={`text-sm tabular-nums ${accent || "text-neutral-700"}`}>{value ?? "—"}</div>
    </div>
  );
}

/* ---------- PO vs Invoice line-item mapping ---------- */
function LineItemMapping({ po, validation }) {
  if (!po) {
    return <div className="text-xs text-neutral-400">PO details unavailable.</div>;
  }
  const poLines = po.line_items || [];
  const matches = validation?.matches || [];
  const mismatches = validation?.mismatches || [];
  const unmatched = validation?.unmatched_invoice_lines || [];
  const ancillary = validation?.ancillary_invoice_lines || [];

  const byIdx = new Map();
  for (const m of matches) if (m.po_line_index != null) byIdx.set(m.po_line_index, { ...m, kind: "match" });
  for (const m of mismatches) if (m.po_line_index != null) byIdx.set(m.po_line_index, { ...m, kind: "mismatch" });

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-neutral-50 border border-neutral-200 rounded-sm text-[10px] uppercase tracking-wider text-neutral-500">
        <div className="col-span-5">PO Line</div>
        <div className="col-span-1 text-right">Qty</div>
        <div className="col-span-1 text-right">₹</div>
        <div className="col-span-5">Invoice Line</div>
      </div>
      {poLines.length === 0 && <div className="text-xs text-neutral-400 py-3">No line items on PO.</div>}
      {poLines.map((p, i) => {
        const ml = byIdx.get(i);
        const invQty = ml?.invoice_quantity;
        const invDesc = ml?.invoice_description || (ml ? p.description : "—");
        const kindColor =
          ml?.kind === "match" ? "text-[#137333]" :
          ml?.kind === "mismatch" ? "text-[#C5221F]" :
          "text-neutral-400";
        return (
          <div key={i} className="grid grid-cols-12 gap-2 px-3 py-2 border border-neutral-100 rounded-sm items-center text-xs" data-testid={`mapping-po-${i}`}>
            <div className="col-span-5 text-neutral-800 truncate">{p.description}</div>
            <div className="col-span-1 text-right tabular-nums text-neutral-600">{fmtQty(p.quantity)}</div>
            <div className="col-span-1 text-right tabular-nums text-neutral-600">{fmtINR(p.unit_price)}</div>
            <div className={`col-span-5 truncate ${kindColor}`}>
              {ml ? (
                <>
                  <span className="tabular-nums">{invDesc}</span>
                  <span className="tabular-nums ml-1 text-neutral-500">· {fmtQty(invQty)} {ml.unit || ""}</span>
                  {ml.reason && <span className="ml-1 text-[10px] text-[#C5221F]">({ml.reason})</span>}
                </>
              ) : (
                <span className="text-neutral-300 italic">No invoice line matched</span>
              )}
            </div>
          </div>
        );
      })}
      {unmatched.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-[#C5221F] mb-1">Invoice lines NOT on PO</div>
          {unmatched.map((u, k) => (
            <div key={k} className="text-xs text-[#C5221F] px-3 py-1.5 bg-[#FCE8E6] border border-[#F3B2AC] rounded-sm mb-1" data-testid={`mapping-unmatched-${k}`}>
              {u.description} · Qty {fmtQty(u.quantity)}
            </div>
          ))}
        </div>
      )}
      {ancillary.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Ancillary charges (auto-accepted)</div>
          {ancillary.map((u, k) => (
            <div key={k} className="text-xs text-neutral-600 px-3 py-1.5 bg-neutral-50 border border-neutral-200 rounded-sm mb-1" data-testid={`mapping-ancillary-${k}`}>
              {u.description} · Qty {fmtQty(u.quantity)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
